/**
 * @fileoverview JWT-aware API Client for Vue.js
 *
 * This client intercepts requests and responses to automatically:
 * 1. Attach the current access token to every outgoing request.
 * 2. Handle a 401 Unauthorized response by attempting to use the refresh token.
 * 3. Save the new access token if the refresh is successful and retry the original request.
 * 4. Force logout if the refresh token is invalid or expired.
 */

const STYLIST_STORAGE_KEY = 'currentStylist';
const BASE_API_URL = 'https://api-afrobraids.inchtechs.com/api'; 

/**
 * Retrieves the current session data (including tokens) from local storage.
 * @returns {object | null} The stylist session object or null.
 */
function getSession() {
    try {
        const sessionJson = localStorage.getItem(STYLIST_STORAGE_KEY);
        return sessionJson ? JSON.parse(sessionJson) : null;
    } catch (e) {
        console.error("Error retrieving session from localStorage:", e);
        return null;
    }
}

/**
 * Saves a new access token to the local session storage.
 * @param {string} newAccessToken - The newly generated access token.
 */
function saveNewAccessToken(newAccessToken) {
    const session = getSession();
    if (session) {
        session.access_token = newAccessToken;
        localStorage.setItem(STYLIST_STORAGE_KEY, JSON.stringify(session));
    }
}

/**
 * Clears session data and forces a logout (e.g., redirect to login page).
 */
function forceLogout() {
    localStorage.removeItem(STYLIST_STORAGE_KEY);
    console.log("Session expired. Logging out and redirecting to login...");
}


async function refreshAccessToken(refreshToken) {
    console.log("Attempting to refresh token...");
    try {
        const response = await fetch(`${BASE_API_URL}/auth/refresh`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${refreshToken}`,
                'Content-Type': 'application/json'
            },
        });

        if (response.ok) {
            const data = await response.json();
            console.log("Token refreshed successfully.");
            return data.access_token;
        } else if (response.status === 401 || response.status === 422) {
            // 401 or 422 (Unprocessable Entity from JWT-Extended for invalid token) means refresh token failed.
            forceLogout();
            return null;
        } else {
            console.error("Failed to refresh token with status:", response.status);
            return null;
        }
    } catch (error) {
        console.error("Network error during token refresh:", error);
        return null;
    }
}


export async function apiClient(url, config = {}) {
    const session = getSession();
    let accessToken = session ? session.access_token : null;
    const refreshToken = session ? session.refresh_token : null;
    let headers = config.headers || {};

    // 1. Attach Access Token to Request if available
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
    }

    config.headers = headers;

    let response; // IMPORTANT: Declare 'response' outside the try block

    try {
        // --- ATTEMPT 1: Execute original request with current access token ---
        response = await fetch(`${BASE_API_URL}${url}`, config); // Assign to outer 'response' variable

        // 2. Check for 401 Unauthorized Error
        if (response.status === 401 && refreshToken) {
            console.warn("Access token expired (401). Initiating refresh cycle.");

            const newAccessToken = await refreshAccessToken(refreshToken);

            if (newAccessToken) {
                // 3. Save the new token and retry
                saveNewAccessToken(newAccessToken);
                
                // 4. Retry the original request with the new token
                console.log("Retrying original request with new token...");
                config.headers['Authorization'] = `Bearer ${newAccessToken}`;
                response = await fetch(`${BASE_API_URL}${url}`, config);
            } else {
                // Refresh failed or forced logout
                return Promise.reject(new Error("Session expired or invalid refresh token."));
            }
        }

        // --- 5. Handle Final Response (Unified Logic) ---
        
        if (response.status === 204) {
            return { message: "Success (No Content)" };
        }
        
        const contentType = response.headers.get("content-type");
        const isJson = contentType && contentType.includes("application/json");

        if (!isJson && !response.ok) {
             // If we get a non-2xx status AND non-JSON (like an HTML error page)
             console.error("Received unexpected non-JSON response on failure:", response.status);
             return Promise.reject(new Error(`Server Error (${response.status}): Received unexpected HTML/text. Session may be invalid.`));
        }
        
        // Safely parse JSON
        const data = await response.json();
        
        if (response.ok) {
            return data;
        } else {
            // Handle structured JSON API errors (4xx, 5xx)
            return Promise.reject(data.error || data.message || "An API error occurred");
        }

    } catch (error) {
        // This catch block handles network errors and the SyntaxError from response.json()
        console.error("API client error (Network/Parsing):", error);

        // If 'error' is the SyntaxError from response.json(), throw a custom message
        if (error instanceof SyntaxError) {
             return Promise.reject(new Error("Failed to parse JSON response. Server likely returned HTML/text content."));
        }
        
        // Throw the original network error
        return Promise.reject(error);
    }
}
